import { useMemo, useState } from 'react';

import { BusinessSubscriptionModel } from '@pukka/api';
import { Button, Icon, Modal, SelectV2, Tab, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { BillingPersistedState, BillingState } from '../Billing.state';
import { BillingPriceItem, BillingPriceSummaryDetail, BillingPriceSummaryDiscount } from '../BillingReceipt';
import { getRecurringDiscountDescription, getTabsFromData } from '../utils';

import { RecurringPaymentState } from './RecurringPayment.state';

interface ModifyBillingModal {
  readonly open?: boolean;
  readonly onToggleModal?: (open: boolean) => void;
  readonly PersistedState?: typeof BillingPersistedState;
  readonly State?: typeof BillingState;
  readonly RecurringState?: typeof RecurringPaymentState;
}

function ModifyBillingModal({
  open,
  PersistedState = BillingPersistedState,
  State = BillingState,
  RecurringState = RecurringPaymentState,
  onToggleModal,
}: ModifyBillingModal) {
  const { recurring } = useSnapshot(PersistedState);
  const { interval, plan } = recurring || {};
  const { isRecurringFetching, calculations } = useSnapshot(State);
  const { isChangingPackage } = useSnapshot(RecurringState);
  const { onChangePackage } = RecurringState;
  const [tab, setTab] = useState(interval as string);
  const tabs = useMemo(() => getTabsFromData(calculations as BusinessSubscriptionModel.BillingCalculation[]), [calculations]);

  const data = calculations.filter((d) => d.is_recurring).find((d) => d.month === (tab === 'monthly' ? 1 : 12));

  if (!data) {
    return null;
  }

  const employeeSlotCount = data.employee_slot?.count ?? 0;
  const employeeSlots = {
    originalPrice: data.employee_slot?.original_price ?? 0,
    finalPrice: data.employee_slot?.final_price ?? 0,
    unit: '/slot/mo',
  };
  const outletCount = data.outlets?.length ?? 0;
  const outlets = {
    originalPrice: data.original_package_deal_price_per_month ?? 0,
    finalPrice: data.final_package_deal_price_per_month ?? 0,
    unit: '/outlet/mo',
    description: data.package_deal_display_name,
  };

  const total = {
    originalPrice: data.original_total_price ?? 0,
    finalPrice: data.final_total_price ?? 0,
    discount: data.discount_percentage,
    description: getRecurringDiscountDescription(tab, data.discount_percentage),
  };

  return (
    <Modal size="lg" unwrap open={open} onOpenChange={onToggleModal}>
      <Modal.Content className="max-w-[632px] bg-neutral-50">
        <Modal.Title className="flex-row justify-between">
          Modify Billing
          <Modal.Trigger asChild>
            <Button variant="nude" size="none" className="rounded-md">
              <Icon icon="close" color="white" />
            </Button>
          </Modal.Trigger>
        </Modal.Title>
        <Modal.Body className="m-0 max-h-none p-6">
          <div className="flex flex-col gap-2.5">
            <Tab.Billing data={tabs} value={tab} onChange={setTab} />
            <div className="flex flex-row items-center justify-between rounded-xl bg-neutral-100 px-8 py-3">
              <Text variant="subtitle-2" weight="medium" className="text-neutral-700">
                Moka Package
              </Text>
              <SelectV2 disabled value={plan} className="w-48 border-neutral-300 bg-neutral-200">
                <SelectV2.Option>{plan}</SelectV2.Option>
              </SelectV2>
            </div>
            <div className="rounded-xl bg-white">
              {employeeSlotCount ? (
                <BillingPriceItem
                  title="Staff Slots"
                  subTitle={`${employeeSlotCount} Slots`}
                  originalPrice={employeeSlots?.originalPrice}
                  finalPrice={employeeSlots?.finalPrice}
                  priceSuffix={employeeSlots?.unit}
                  loading={isRecurringFetching}
                  className="grid-cols-2"
                />
              ) : null}
              {outletCount ? (
                <BillingPriceItem
                  title={`Outlets (${outletCount})`}
                  subTitle={plan}
                  originalPrice={outlets?.originalPrice}
                  finalPrice={outlets?.finalPrice}
                  priceSuffix={outlets?.unit}
                  loading={isRecurringFetching}
                  className="grid-cols-2"
                />
              ) : null}
            </div>
            <div className="rounded-xl bg-white px-8 py-2">
              <BillingPriceSummaryDetail loading={isRecurringFetching} bigLabel label="Total" price={total?.finalPrice ?? 0}>
                {total?.finalPrice !== total?.originalPrice ? (
                  <BillingPriceSummaryDiscount
                    loading={isRecurringFetching}
                    price={total?.originalPrice}
                    description={total?.description}
                  />
                ) : null}
              </BillingPriceSummaryDetail>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Button
            block
            loading={isChangingPackage}
            disabled={interval === tab}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={onChangePackage.bind(RecurringPaymentState, data, undefined)}
          >
            Change Package
          </Modal.Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

export { ModifyBillingModal };
